import { useEffect, useState } from 'react';
import { AxiosInstance } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { confirmLink, getLinkReview, Issue, LinkReview } from '../../api/issues';
import { describeError } from '../../api/requeue';
import { Modal } from '../Modal/Modal';
import { LINK_OUTCOME, linkableEntry, linkFields, money } from './exactCompare';
import './RelinkDialog.scss';

export interface ILinkDialogProps {
    axios: AxiosInstance;
    issue: Issue | null;
    onClose: () => void;
    /** Called after a link went through, to reload the lists. */
    onDone: () => void;
}

/**
 * Tying a Chargebee document to the booking Exact has of it already, decided by a person. Exact
 * booked it while the connector got an error, or someone booked it by hand; the connector does
 * not know, and offering it again would book it twice. On top what is tied now; below the
 * booking field by field next to the document. Nothing happens until someone confirmed it is the
 * same document; the server reads Exact and the relation again before it writes.
 */
export const LinkDialog = ({ axios, issue, onClose, onDone }: ILinkDialogProps) => {
    const [review, setReview] = useState<LinkReview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<string | null>(null);

    useEffect(() => {
        setReview(null);
        setResult(null);
        setError(null);
        setVerified(false);

        if (!issue) {
            return;
        }

        getLinkReview(axios, issue.customer, issue.environment, issue.key)
            .then(setReview)
            .catch((ex) => setError(describeError(ex)));
    }, [axios, issue]);

    // Only one booking can be tied; with more, the problem says why not.
    const entry = linkableEntry(review);
    const fields = entry ? linkFields(review?.chargebee, entry) : [];
    const differs = fields.some((f) => f.match === false);

    const confirm = () => {
        if (!issue || !entry?.entryId) {
            return;
        }

        setBusy(true);
        setError(null);

        confirmLink(axios, issue.customer, issue.environment, issue.key, entry.entryId)
            .then((outcome) => {
                setResult(`${LINK_OUTCOME[outcome.state] ?? outcome.state}${outcome.note ? ` ${outcome.note}` : ''}`);
                onDone();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(false));
    };

    const content = (
        <div className="relink">
            {!review && !error && (
                <p className="relink__loading">
                    <FontAwesomeIcon icon="spinner" spin /> Document en boekingen ophalen…
                </p>
            )}
            {error && <p className="relink__alert relink__alert--error">{error}</p>}

            {review && (
                <>
                    <div className="relink__context">
                        <div className="relink__party">
                            <span className="relink__label">Chargebee</span>
                            <strong>{review.object}</strong>
                            <span className="relink__sub">{review.chargebee?.exists ? money(review.chargebee.total, review.chargebee.currency) : 'Onbekend in Chargebee'}</span>
                        </div>
                        <FontAwesomeIcon icon="arrow-right" className="relink__arrow" />
                        <div className={`relink__party${entry ? '' : ' relink__party--gone'}`}>
                            <span className="relink__label">Al in Exact</span>
                            <strong>{entry ? `Boekstuk ${entry.entryNumber ?? '?'}` : `${review.entries.length} boekingen`}</strong>
                            <span className="relink__sub">De koppeling van de connector kent deze boeking niet</span>
                        </div>
                    </div>

                    {review.problem && <p className="relink__alert">{review.problem}</p>}

                    {entry && (
                        <table className="relink__table">
                            <thead>
                                <tr>
                                    <th aria-label="Veld" />
                                    <th>Chargebee</th>
                                    <th>Exact boekstuk {entry.entryNumber}</th>
                                    <th aria-label="Overeenkomst" />
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map((f) => (
                                    <tr key={f.label} className={f.match === true ? 'is-same' : f.match === false ? 'is-different' : undefined}>
                                        <th>{f.label}</th>
                                        <td>{f.chargebee || <span className="relink__none">–</span>}</td>
                                        <td>{f.exact || <span className="relink__none">–</span>}</td>
                                        <td className="relink__verdict">
                                            {f.match === true && <FontAwesomeIcon icon="circle-check" title="Gelijk" />}
                                            {f.match === false && <FontAwesomeIcon icon="circle-xmark" title="Wijkt af" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {entry && differs && !result && (
                        <p className="relink__alert">
                            Niet alles komt overeen. Koppel alleen als je zeker weet dat dit dezelfde {review.object.split(' ')[0]} is; zoek anders eerst uit waar het verschil
                            vandaan komt.
                        </p>
                    )}

                    {result ? (
                        <p className="relink__alert relink__alert--ok">
                            <FontAwesomeIcon icon="circle-check" /> {result}
                        </p>
                    ) : (
                        entry && (
                            <label className="relink__verify">
                                <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
                                <span>
                                    Ik heb gecontroleerd dat <strong>boekstuk {entry.entryNumber}</strong> in Exact {review.object} is.
                                </span>
                            </label>
                        )
                    )}
                </>
            )}
        </div>
    );

    const footer = (
        <div className="d-flex gap-2 ms-auto">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                {result ? 'Sluiten' : 'Annuleren'}
            </button>
            {!result && (
                <button type="button" className="btn btn-sm btn-primary" disabled={!entry || !verified || busy} onClick={confirm}>
                    <FontAwesomeIcon icon={busy ? 'spinner' : 'link'} spin={busy} /> Koppelen
                </button>
            )}
        </div>
    );

    return <Modal show={issue !== null} handleClose={onClose} title="Koppelen aan de boeking in Exact" content={content} footer={footer} wide />;
};
